import React, { useLayoutEffect, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { LayoutDashboard, Handshake, Users, Wallet, ChartNoAxesCombined, Send, SlidersHorizontal, Bot, CreditCard, Settings, ShieldCheck, LifeBuoy, Gift } from 'lucide-react';

// Keep the original nodes: their event handlers, form values and API modules stay attached.
export function ExistingContent({ element, resetScroll = false }) {
    const host = useRef(null);
    useLayoutEffect(() => {
        if (!element || !host.current) return;
        const marker = document.createComment('Original component location');
        element.before(marker);
        host.current.append(element);
        if (resetScroll) element.scrollTop = 0;
        return () => { marker.replaceWith(element); };
    }, [element, resetScroll]);
    return <div ref={host} className="ui-existing-content" />;
}

function ExistingDialog({ popup }) {
    const [open, setOpen] = useState(popup.classList.contains('active'));
    const readTitle = () => popup.querySelector('.popup-header p, .popup-header h2, h3')?.childNodes[0]?.textContent?.trim() || 'TaKorna';
    const [title, setTitle] = useState(readTitle);
    const trigger = useRef(null);
    useLayoutEffect(() => {
        const observer = new MutationObserver(() => {
            const active = popup.classList.contains('active');
            if (active && !popup.closest('[data-slot="dialog-content"]')) trigger.current = document.activeElement;
            setOpen(active);
            setTitle(readTitle());
        });
        observer.observe(popup, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, [popup]);
    return <Dialog open={open} onOpenChange={value => { if (!value && popup.dataset.paymentBusy !== 'true') popup.classList.remove('active'); }}>
        <DialogContent className="ui-dialog-shell" showCloseButton={false}
            onEscapeKeyDown={event => { if (popup.dataset.paymentBusy === 'true') event.preventDefault(); }}
            onInteractOutside={event => { if (popup.dataset.paymentBusy === 'true') event.preventDefault(); }}
            onCloseAutoFocus={event => { event.preventDefault(); trigger.current?.focus?.(); }}>
            <DialogTitle className="sr-only">{title}</DialogTitle>
            <DialogDescription className="sr-only">Проверьте данные перед сохранением.</DialogDescription>
            <ExistingContent element={popup} />
        </DialogContent>
    </Dialog>;
}

export function ExistingDialogs() {
    const [popups] = useState(() => [...document.querySelectorAll('.popup')]);
    return popups.map((popup, index) => <ExistingDialog key={index} popup={popup} />);
}

function componentClasses(component) {
    const template = document.createElement('template');
    template.innerHTML = renderToStaticMarkup(component);
    return template.content.firstElementChild.className.split(/\s+/);
}

export function enhanceControls() {
    const classes = {
        button: componentClasses(<Button variant="outline" />),
        input: componentClasses(<Input />),
        textarea: componentClasses(<Textarea />),
    };
    const icons = { overview: LayoutDashboard, home: LayoutDashboard, deals: Handshake, clients: Users, finances: Wallet, reports: ChartNoAxesCombined, newsletters: Send, configuration: SlidersHorizontal, autoresponder: Bot, subscription: CreditCard, settings: Settings, admin: ShieldCheck, partnership: Users };
    document.querySelectorAll('#sidebar button[data-page], #support-btn, #promo-btn').forEach(button => {
        if (button.dataset.page === 'sms') return;
        const Icon = icons[button.dataset.page] || (button.id === 'support-btn' ? LifeBuoy : Gift);
        const oldIcon = button.querySelector('svg');
        if (Icon && oldIcon) {
            const template = document.createElement('template');
            template.innerHTML = renderToStaticMarkup(<Icon size={18} aria-hidden="true" />);
            oldIcon.replaceWith(template.content.firstElementChild);
        }
    });
    function enhance(root) {
        if (!(root instanceof Element) && root !== document) return;
        const actionSelector = '[data-popup],.btn,.toggle-password,svg.trash,.close-popup,.add-param-btn,.remove-param-btn,#pageAutoresponder svg.pause,#pageAutoresponder svg.play';
        const actions = [...root.querySelectorAll(actionSelector)];
        if (root.matches?.(actionSelector)) actions.unshift(root);
        for (const action of actions) {
            if (!action.textContent.trim() && !action.hasAttribute('aria-label')) {
                action.setAttribute('aria-label', action.matches('.toggle-password') ? 'Показать пароль' : action.matches('.close-popup') ? 'Закрыть' : action.matches('.add-param-btn') ? 'Добавить параметр' : action.matches('.trash') ? 'Удалить' : action.matches('.btn.success') ? 'Завершённые сделки' : action.matches('.btn.cancel') ? 'Отменённые сделки' : action.matches('.btn.active') ? 'Активные сделки' : action.getAttribute('title') || 'Открыть');
            }
            if (action.matches('.remove-param-btn')) action.setAttribute('aria-label', 'Удалить параметр');
            if (action.matches('svg.pause')) action.setAttribute('aria-label', 'Приостановить правило');
            if (action.matches('svg.play')) action.setAttribute('aria-label', 'Включить правило');
            if (action.matches('svg.edit')) action.setAttribute('aria-label', 'Редактировать');
            if (action.matches('button,a,input') || action.closest('[data-react-ui]') || action.dataset.actionEnhanced) continue;
            action.dataset.actionEnhanced = 'true';
            action.classList.add('legacy-action');
            action.setAttribute('role', 'button');
            action.tabIndex = 0;
            action.addEventListener('keydown', event => {
                if (event.target === action && ['Enter', ' '].includes(event.key)) { event.preventDefault(); action.dispatchEvent(new MouseEvent('click', { bubbles: true })); }
            });
        }
        for (const field of root.querySelectorAll('input,textarea,select')) {
            const labelText = [...(field.labels || [])].some(label => label.textContent.trim());
            if (!labelText && !field.hasAttribute('aria-label') && field.placeholder) field.setAttribute('aria-label', field.placeholder);
            if (field.matches('input[type=checkbox]') && field.labels?.length && getComputedStyle(field).display === 'none') {
                field.classList.add('accessible-toggle');
                if (!labelText && !field.hasAttribute('aria-label')) field.setAttribute('aria-label', field.parentElement.textContent.trim());
            }
            if (field.matches('select') && !field.hasAttribute('aria-label')) {
                if (field.matches('.notif-unit')) field.setAttribute('aria-label', 'Единица времени');
                else if (field.closest('.third,.param-row')) field.setAttribute('aria-label', 'Тип значения');
            }
        }
        root.querySelectorAll('button.deal-delete-btn').forEach(button => button.setAttribute('aria-label', 'Удалить сделку'));
        root.querySelectorAll('.remove-notif-btn').forEach(button => button.setAttribute('aria-label', 'Удалить уведомление'));
        for (const input of root.querySelectorAll('.login-page input[type=password]')) {
            if (input.closest('.password-field')) continue;
            const field = document.createElement('div');
            field.className = 'password-field';
            input.before(field);
            field.append(input);
            const toggle = document.createElement('button');
            toggle.type = 'button';
            toggle.className = 'password-toggle';
            toggle.setAttribute('aria-label', 'Показать пароль');
            toggle.setAttribute('aria-pressed', 'false');
            toggle.innerHTML = '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>';
            toggle.addEventListener('click', () => {
                const shown = input.type === 'password';
                input.type = shown ? 'text' : 'password';
                toggle.setAttribute('aria-pressed', String(shown));
                toggle.setAttribute('aria-label', shown ? 'Скрыть пароль' : 'Показать пароль');
            });
            field.append(toggle);
        }
        const elements = [...root.querySelectorAll('button,input,textarea')];
        if (root.matches?.('button,input,textarea')) elements.unshift(root);
        for (const element of elements) {
            if (element.dataset.uiEnhanced || element.matches('.marker') || element.closest('[data-react-ui]')) continue;
            const kind = element.tagName.toLowerCase();
            if (kind === 'input' && ['checkbox','radio','range','hidden','file','color'].includes(element.type)) continue;
            element.classList.add(`legacy-${kind}`, ...classes[kind]);
            element.dataset.slot = kind;
            element.dataset.uiEnhanced = 'true';
        }
    }
    enhance(document);
    const observer = new MutationObserver(records => {
        for (const record of records) for (const node of record.addedNodes) enhance(node);
    });
    observer.observe(document.body, { childList: true, subtree: true });
}
